import React, { useEffect, useState } from "react";
import { Plus, Trash2, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import api, { taka, formatApiErrorDetail } from "@/lib/api";
import { effectivePrice } from "@/lib/pricing";
import { downloadSaleInvoice } from "@/lib/invoice";
import { Modal, Field, inputCls, Th, Td, Empty, fmtDate } from "@/pages/admin/AdminUI";

export default function AdminSales() {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [lines, setLines] = useState([{ product_id: "", quantity: 1, price: 0 }]);
  const [meta, setMeta] = useState({ customer_id: "", customer_name: "খুচরা কাস্টমার", discount: 0, delivery_charge: 0, paid_amount: 0, payment_method: "cash" });

  const load = () => {
    api.get("/admin/sales").then((r) => setSales(r.data));
    api.get("/products", { params: { include_inactive: true } }).then((r) => setProducts(r.data));
    api.get("/admin/customers").then((r) => setCustomers(r.data));
  };
  useEffect(() => { load(); }, []);

  const selCustomer = customers.find((c) => c.id === meta.customer_id);
  const wholesale = selCustomer?.type === "wholesale";
  const priceFor = (p, qty) => (p ? effectivePrice(p, Number(qty) || 1, { wholesale }) : 0);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => {
    if (idx !== i) return l;
    const nl = { ...l, [k]: v };
    if (k === "quantity") { const p = products.find((x) => x.id === nl.product_id); nl.price = priceFor(p, v); }
    return nl;
  }));
  const onProduct = (i, pid) => { const p = products.find((x) => x.id === pid); setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, product_id: pid, price: priceFor(p, l.quantity) } : l)); };
  const onCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    const w = c?.type === "wholesale";
    setMeta((m) => ({ ...m, customer_id: cid, customer_name: c ? c.name : "খুচরা কাস্টমার" }));
    setLines((ls) => ls.map((l) => { const p = products.find((x) => x.id === l.product_id); return p ? { ...l, price: effectivePrice(p, Number(l.quantity) || 1, { wholesale: w }) } : l; }));
  };
  const addLine = () => setLines((ls) => [...ls, { product_id: "", quantity: 1, price: 0 }]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const subtotal = lines.reduce((s, l) => s + Number(l.quantity) * Number(l.price), 0);
  const total = subtotal - Number(meta.discount) + Number(meta.delivery_charge);

  const save = async () => {
    const items = lines.filter((l) => l.product_id).map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), price: Number(l.price) }));
    if (!items.length) return toast.error("অন্তত একটি পণ্য যোগ করুন");
    try {
      await api.post("/admin/sales", { ...meta, discount: Number(meta.discount), delivery_charge: Number(meta.delivery_charge), paid_amount: Number(meta.paid_amount), items });
      toast.success("বিক্রয় রেকর্ড হয়েছে"); setOpen(false);
      setLines([{ product_id: "", quantity: 1, price: 0 }]); setMeta({ customer_id: "", customer_name: "খুচরা কাস্টমার", discount: 0, delivery_charge: 0, paid_amount: 0, payment_method: "cash" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-[#1E3A2B]">বিক্রয় (POS)</h1>
        <button onClick={() => setOpen(true)} className="bg-[#1E3A2B] text-white h-11 px-4 rounded-xl flex items-center gap-2 font-medium" data-testid="add-sale-btn"><Plus size={18} /> নতুন বিক্রয়</button>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-stone-50 border-b border-stone-200"><tr><Th>ইনভয়েস</Th><Th>কাস্টমার</Th><Th>উৎস</Th><Th>তারিখ</Th><Th>মোট</Th><Th>বাকি</Th><Th></Th></tr></thead>
          <tbody className="divide-y divide-stone-100">
            {sales.map((s) => (
              <tr key={s.id} data-testid={`sale-row-${s.invoice_no}`}>
                <Td className="font-semibold text-[#1E3A2B]">{s.invoice_no}</Td>
                <Td>{s.customer_name}</Td>
                <Td><span className={`text-xs px-2 py-0.5 rounded-full ${s.source === "online" ? "bg-blue-100 text-blue-700" : "bg-stone-100 text-stone-600"}`}>{s.source === "online" ? "অনলাইন" : "ম্যানুয়াল"}</span></Td>
                <Td>{fmtDate(s.date)}</Td>
                <Td className="font-semibold">{taka(s.total)}</Td>
                <Td className={s.due > 0 ? "text-red-600 font-semibold" : "text-emerald-700"}>{taka(s.due)}</Td>
                <Td><button onClick={() => setView(s)} className="p-2 text-blue-600"><Printer size={16} /></button></Td>
              </tr>
            ))}
          </tbody>
        </table>
        {sales.length === 0 && <Empty text="কোনো বিক্রয় নেই" />}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="নতুন বিক্রয়" wide>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field label="কাস্টমার (তালিকা)">
            <select className={inputCls} value={meta.customer_id} onChange={(e) => onCustomer(e.target.value)} data-testid="sale-customer">
              <option value="">খুচরা কাস্টমার</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.type === "wholesale" && !c.name.includes("পাইকারি") ? " (পাইকারি)" : ""}</option>)}
            </select>
          </Field>
          <Field label="পেমেন্ট পদ্ধতি"><select className={inputCls} value={meta.payment_method} onChange={(e) => setMeta((m) => ({ ...m, payment_method: e.target.value }))}><option value="cash">নগদ</option><option value="bkash">বিকাশ</option><option value="nagad">নগদ (মোবাইল)</option><option value="bank">ব্যাংক</option></select></Field>
        </div>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <select className={`${inputCls} col-span-5`} value={l.product_id} onChange={(e) => onProduct(i, e.target.value)} data-testid={`sale-line-product-${i}`}><option value="">পণ্য</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name_bn}</option>)}</select>
              <input type="number" className={`${inputCls} col-span-2`} placeholder="পরিমাণ" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`sale-line-qty-${i}`} />
              <input type="number" className={`${inputCls} col-span-3`} placeholder="দাম" value={l.price} onChange={(e) => setLine(i, "price", e.target.value)} />
              <div className="col-span-1 text-sm font-medium text-right">{Math.round(l.quantity * l.price)}</div>
              <button onClick={() => removeLine(i)} className="col-span-1 text-red-500"><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={addLine} className="text-sm text-emerald-700 font-medium flex items-center gap-1" data-testid="add-sale-line"><Plus size={14} /> পণ্য যোগ</button>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <Field label="ছাড়"><input type="number" className={inputCls} value={meta.discount} onChange={(e) => setMeta((m) => ({ ...m, discount: e.target.value }))} /></Field>
          <Field label="ডেলিভারি"><input type="number" className={inputCls} value={meta.delivery_charge} onChange={(e) => setMeta((m) => ({ ...m, delivery_charge: e.target.value }))} /></Field>
          <Field label="পরিশোধিত"><input type="number" className={inputCls} value={meta.paid_amount} onChange={(e) => setMeta((m) => ({ ...m, paid_amount: e.target.value }))} data-testid="sale-paid" /></Field>
        </div>
        <div className="mt-3 bg-emerald-50 rounded-xl p-3 flex justify-between font-semibold text-emerald-900"><span>সর্বমোট: {taka(total)}</span><span>বাকি: {taka(total - Number(meta.paid_amount))}</span></div>
        <button onClick={save} className="mt-4 w-full bg-[#1E3A2B] text-white h-11 rounded-xl font-medium" data-testid="save-sale-btn">বিক্রয় সংরক্ষণ (স্টক কমবে)</button>
      </Modal>

      <Modal open={!!view} onClose={() => setView(null)} title={view?.invoice_no}>
        {view && (
          <div className="space-y-2 text-sm" id="sale-invoice">
            <div className="font-semibold text-[#1E3A2B]">{view.customer_name}</div>
            <div className="text-stone-500">{fmtDate(view.date)}</div>
            <table className="w-full border-t border-stone-200 mt-2">
              <tbody>{view.items.map((it, i) => <tr key={i} className="border-b border-stone-100"><td className="py-1">{it.name_bn} × {it.quantity}</td><td className="text-right">{taka(it.line_total)}</td></tr>)}</tbody>
            </table>
            <div className="flex justify-between font-bold text-[#1E3A2B] pt-1"><span>মোট</span><span>{taka(view.total)}</span></div>
            <div className="flex justify-between"><span>পরিশোধিত</span><span>{taka(view.paid_amount)}</span></div>
            <div className="flex justify-between text-red-600"><span>বাকি</span><span>{taka(view.due)}</span></div>
            <button onClick={() => window.print()} className="w-full bg-stone-800 text-white h-11 rounded-xl mt-2 flex items-center justify-center gap-2"><Printer size={16} /> প্রিন্ট</button>
            <button onClick={() => downloadSaleInvoice(view)} className="w-full bg-[#CA8A04] text-white h-11 rounded-xl mt-2 flex items-center justify-center gap-2" data-testid="download-sale-pdf"><Download size={16} /> PDF ডাউনলোড</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
